// client/src/App.jsx
// OrbitCal — Main Application Shell
// Rendering is state-driven: auth → setup → calendar.

import { useState, useEffect, useCallback } from "react";
import { api } from "./api.js";
import { CalendarView } from "./CalendarView.jsx";
import { AuthPage } from "./AuthPage.jsx";
import { SetupPage } from "./SetupPage.jsx";

export function App() {
  const [user, setUser] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check auth on mount
  useEffect(() => {
    if (api.isLoggedIn()) {
      api.me()
        .then(data => {
          setUser(data.user);
          setOrgs(data.organizations);
          if (data.organizations.length > 0) {
            setCurrentOrg(data.organizations[0]);
          }
        })
        .catch(() => {
          api.clearToken();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = useCallback((data) => {
    api.setToken(data.token);
    setUser(data.user);
    setOrgs(data.organizations || []);
    setCurrentOrg(data.organizations?.[0] || null);
  }, []);

  const handleLogout = useCallback(() => {
    api.logout().catch(() => {});
    api.clearToken();
    setUser(null);
    setOrgs([]);
    setCurrentOrg(null);
  }, []);

  const handleOrgCreated = useCallback((org) => {
    setOrgs(prev => [...prev, org]);
    setCurrentOrg(org);
  }, []);

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#f4f6fb" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: "linear-gradient(135deg, #3b82f6, #8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 800, color: "#fff", margin: "0 auto 12px" }}>OC</div>
          <div style={{ color: "#64748b", fontSize: 13 }}>Loading...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <AuthPage onLogin={handleLogin} />;
  }

  if (!currentOrg) {
    return <SetupPage user={user} onOrgCreated={handleOrgCreated} onOrgJoined={handleOrgCreated} onLogout={handleLogout} />;
  }

  return (
    <CalendarView
      user={user}
      org={currentOrg}
      orgs={orgs}
      onSwitchOrg={setCurrentOrg}
      onLogout={handleLogout}
    />
  );
}
